import { useRef, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { UseFormReset } from "react-hook-form";
import { FormData } from "MVVM/Views/ProfileSettings/ThirdPartySettings/types";

interface UseTrackUnsavedChangesProps {
  view: string;
  setView: (view: string) => void;
  formState: {
    isDirty: boolean;
  };
  reset: UseFormReset<FormData>;
}

export const useTrackUnsavedChanges = ({
  view,
  setView,
  formState,
  reset,
}: UseTrackUnsavedChangesProps) => {
  const history = useHistory();
  const location = useLocation();
  const previousView = useRef<null | string>(null);

  const showUnsavedChangesPrompt = () => {
    return window.confirm(
      "You have unsaved changes. Are you sure you want to leave this page?",
    );
  };

  useEffect(() => {
    // block reload of page with saving
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (formState.isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Block navigation to new page without saving
    const unblock = history.block((nextLocation) => {
      if (formState.isDirty && nextLocation.pathname !== location.pathname) {
        const confirm = showUnsavedChangesPrompt();
        if (confirm) {
          return;
        } else {
          return false;
        }
      }
      return;
    });

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      unblock();
    };
  }, [formState.isDirty, location.pathname, history]);

  // block changing component based on nested routes in Profile Settings
  useEffect(() => {
    if (
      previousView.current === "thirdPartySettings" &&
      view !== previousView.current &&
      formState.isDirty
    ) {
      const confirm = showUnsavedChangesPrompt();
      if (confirm) {
        reset();
      } else {
        setView("thirdPartySettings");
      }
    }
    previousView.current = view;
  }, [view, formState.isDirty, reset, setView]);
};
